import { ref } from 'vue'
import { DriverService } from './driver'
import { DriverSocketMsg, DriverSocketMsgType } from '@libs/types/message'
import { enableDialog } from '@/components/cdialog/dialog'
import { loadingContent } from '@/components/loading/loading.vue'
import { VK } from '@/define/define'
import { Http } from '@/network/http'
import { DeviceManager, DeviceProtocol } from '../device'
import i18n from '@/languages/i18n'
import { setData } from './data'
import { actionSet } from './action_set'
const { t } = i18n.global
import { compare } from 'compare-versions'
import { isElectron } from '@libs/utils/envUtils'

interface Config {
  Current: number
  List: ConfigList[]
}
interface ConfigList {
  File: string
  Name: string
  Shortcut: VK[]
  ShortcutRet: boolean
}
export interface configModel {
  bg_img: string
  content: string
  desc: string
  id: number
  name: string
  sort: number
  sub_name: string
  valid: boolean
}
class ConfigSet {
  configList = ref<ConfigList[]>([]) // 配置列表
  currentConfig = ref<number>(0) // 当前配置
  private _isShowShortcon = ref(false) // 是否显示快捷键
  get isShowShortcon() {
    return this._isShowShortcon.value
  }
  set isShowShortcon(val: boolean) {
    this._isShowShortcon.value = val
  }
  recommendConfigList = ref<configModel[]>([]) // 推荐配置列表
  changeLock = false // 切换配置锁

  // 是否支持绑定快捷键的功能
  get isFeatureSupported() {
    if (DeviceManager.instance.state.driverProtocol == DeviceProtocol.hid_web) {
      return false
    }
    if (isElectron() && DeviceManager.instance.state.driverServiceVersion != null && compare(DeviceManager.instance.state.driverServiceVersion, '2.1.5', '>=')) {
      return true
    }
    return false
  }

  handelChangeConfig() {
    DriverService.instance.setHandler(DriverSocketMsgType.MTCfgChange, (_) => {
      setData.askData()
      actionSet.resetData()
    })
  }

  handelSwitchConfig() {
    DriverService.instance.setHandler(
      DriverSocketMsgType.MSDeviceSwitchSnapshotStart,
      (_) => {
        loadingContent.value = t('change_config_loading')
      },
      () => {
        loadingContent.value = ''
        enableDialog(t('change_failed'))
      },
    )
    DriverService.instance.setHandler(
      DriverSocketMsgType.MSDeviceSwitchSnapshotEnd,
      (res: Config) => {
        loadingContent.value = ''
        if (DeviceManager.instance.state.driverProtocol == DeviceProtocol.dll_exe) {
          this.currentConfig.value = res.Current
          this.configList.value = res.List
          setData.init()
        }
        enableDialog(t('change_success'))
      },
      () => {
        loadingContent.value = ''
        enableDialog(t('change_failed'))
      },
    )
  }

  async getConfigRecommend() {
    this.recommendConfigList.value = await Http.get<configModel[]>(
      'configRcommends',
      {
        device: DeviceManager.instance.state.currentPid,
      },
      'https://ncus.iyx.fun/',
    )
  }

  apiConfigList() {
    DriverService.instance.askType<Config>(DriverSocketMsgType.MTCfgList).then(async (res: Config) => {
      this.currentConfig.value = res.Current
      this.configList.value = res.List
    })
  }

  /**
   * 仅在 Electron 环境下，将 HID 协议设备的配置槽位同步补齐到 EXE 服务端
   * 解决两端配置数量不一致导致的快捷键绑定越界 (index out of range) 问题
   */
  private async syncHidConfigsToExe() {
    return
    // 增加：检查 EXE 服务是否已就绪且有识别到的物理设备
    const exeDriver = DriverService.instance.exeDriver
    const isSocketOpen = exeDriver.socketService.checkSocketIsOpen()
    const hasExeDevices = DeviceManager.instance.state.deviceList.length > 0

    if (!isSocketOpen) {
      console.warn('[HID] syncHidConfigsToExe skipped: EXE socket not open')
      return
    }

    // if (!hasExeDevices) {
    //   console.warn('[HID] ===============syncHidConfigsToExe skipped: No devices found in EXE service')
    //   return
    // }

    try {
      // 获取 EXE 服务端当前的配置列表
      let exeRes = await exeDriver.socketService.askType<Config>(DriverSocketMsgType.MTCfgList)

      if (!exeRes || !exeRes.List) {
        console.warn('[HID] syncHidConfigsToExe: EXE returned empty config list')
        return
      }

      console.log('[HID] syncHidConfigsToExe', exeRes)
      // 如果 EXE 侧的配置数量少于 HID 侧，说明物理配置文件缺失，需要进行静默补齐
      if (exeRes.List.length < this.configList.value.length) {
        const diff = this.configList.value.length - exeRes.List.length
        for (let i = 0; i < diff; i++) {
          const nextIndex = exeRes.List.length + i
          const configName = this.configList.value[nextIndex]?.Name || `${t('config')}${nextIndex + 1}`

          const addData = {
            Type: DriverSocketMsgType.MTCfgCreate,
            Data: { Value: configName },
          } as DriverSocketMsg<any>

          // 在 EXE 侧创建缺失的配置文件
          exeRes = await exeDriver.socketService.askTypeAndData<Config>(addData)
          console.log('[HID] syncHidConfigsToExe - created missing config', addData, exeRes)

          // 增加：微小延迟，给后端物理磁盘 IO 留出时间，防止连续创建触发 nil pointer 或竞争
          await new Promise((resolve) => setTimeout(resolve, 50))
        }
      }

      // 将 EXE 侧的快捷键回显数据（Shortcut/ShortcutRet）合并到当前的 configList 中
      this.configList.value.forEach((item, index) => {
        if (exeRes.List && exeRes.List[index]) {
          item.Shortcut = exeRes.List[index].Shortcut
          item.ShortcutRet = exeRes.List[index].ShortcutRet
        }
      })
    } catch (e) {
      console.error('syncHidConfigsToExe failed:', e)
    }
  }

  // 添加配置
  async apiAddConfig(): Promise<void> {
    // 构造数据
    const data = {
      Type: DriverSocketMsgType.MTCfgCreate, // 消息类型
      Data: { Value: `${t('config')}${this.configList.value.length + 1}` },
    } as DriverSocketMsg<any>

    // if (isElectron() && DeviceManager.instance.state.driverProtocol == DeviceProtocol.hid_web) {
    //   // 强制在操作前执行同步校验，确保索引一致
    //   await this.syncHidConfigsToExe()
    //   if (DriverService.instance.exeDriver.socketService.checkSocketIsOpen()) {
    //     // 必须先在 EXE 服务中同步创建物理文件，以保证后续在该索引上绑定快捷键时不会报错（index out of range）
    //     await DriverService.instance.exeDriver.socketService.askTypeAndData<Config>(data)
    //   }
    // }
    await this.postData(data)
  }

  apiChangeConfig(index: number) {
    if (this.changeLock) return
    this.changeLock = true
    loadingContent.value = t('config_change_message')
    const data = { Type: DriverSocketMsgType.MTCfgChange, Data: { Value: index } } as DriverSocketMsg<any>
    this.postData(data)
      .then(() => {
        this.updateLoadingAndLock()
        actionSet.resetData()
        enableDialog(t('change_success'))
      })
      .catch(() => {
        this.updateLoadingAndLock()
        enableDialog(t('change_failed'))
      })
  }

  private updateLoadingAndLock() {
    this.changeLock = false
    loadingContent.value = ''
  }

  async apiDelete(index: number): Promise<void> {
    const data = {
      Type: DriverSocketMsgType.MTCfgDelete,
      Data: { Value: index },
    } as DriverSocketMsg<any>
    // if (isElectron() && DeviceManager.instance.state.driverProtocol == DeviceProtocol.hid_web) {
    //   // 强制在操作前执行同步校验，确保索引一致
    //   await this.syncHidConfigsToExe()
    //   if (DriverService.instance.exeDriver.socketService.checkSocketIsOpen()) {
    //     // HID 设备删除配置时，同步删除 EXE 端对应的物理文件，确保列表长度和索引一致
    //     await DriverService.instance.exeDriver.socketService.askTypeAndData<Config>(data)
    //   }
    // }
    await this.postData(data)
  }

  async apiReName(index: number, name: string): Promise<void> {
    const data = {
      Type: DriverSocketMsgType.MTCfgRename,
      Data: { ID: index, Name: name },
    } as DriverSocketMsg<any>
    // if (isElectron() && DeviceManager.instance.state.driverProtocol == DeviceProtocol.hid_web) {
    //   // 强制在操作前执行同步校验，确保索引一致
    //   await this.syncHidConfigsToExe()
    //   if (DriverService.instance.exeDriver.socketService.checkSocketIsOpen()) {
    //     // HID 设备重命名时，同步通知 EXE 端更新对应配置文件的名称元数据
    //     await DriverService.instance.exeDriver.socketService.askTypeAndData<Config>(data)
    //   }
    // }
    await this.postData(data)
  }

  async apiBindShortcut(index: number, shortcut: VK[]): Promise<void> {
    const data = {
      Type: DriverSocketMsgType.MTCfgBindShortcut,
      Data: { ID: index, Name: this.configList.value[index].Name, Shortcut: shortcut },
    } as DriverSocketMsg<any>
    // if (isElectron() && DeviceManager.instance.state.driverProtocol == DeviceProtocol.hid_web) {
    //   // 强制在操作前执行同步校验，确保索引一致
    //   await this.syncHidConfigsToExe()
    //   const exeDriver = DriverService.instance.exeDriver
    //   // 增加：执行绑定前再次确认 EXE 服务可用性
    //   if (exeDriver.socketService.checkSocketIsOpen()) {
    //     // HID 设备的快捷键绑定操作强制分发给 EXE 服务处理（因系统热键注册由 EXE 负责）
    //     const res = await exeDriver.socketService.askTypeAndData<Config>(data)
    //     // 更新该配置项的显示状态：快捷键数组和注册结果（ShortcutRet）
    //     if (res && res.List && res.List[index]) {
    //       this.configList.value[index].Shortcut = res.List[index].Shortcut
    //       this.configList.value[index].ShortcutRet = res.List[index].ShortcutRet
    //     }
    //   } else {
    //     console.warn('[HID] apiBindShortcut skipped: EXE service or device not ready')
    //   }
    // }
    await this.postData(data)
    this.isShowShortcon = false
  }

  private postData(data: DriverSocketMsg<any>): Promise<Config> {
    return new Promise((resolve, reject) => {
      DriverService.instance
        .askTypeAndData<Config>(data)
        .then((res: Config) => {
          this.currentConfig.value = res.Current
          this.configList.value = res.List
          resolve(res)
        })
        .catch((error: any) => {
          reject(error)
        })
    })
  }

  async getExportConfigData(index: number): Promise<string> {
    const data = {
      Type: DriverSocketMsgType.MTCfgExport,
      Data: { Value: index },
    } as DriverSocketMsg<any>
    return await DriverService.instance.askTypeAndData<string>(data)
  }

  // 设置配置预设
  async setConfigPreset(name: string, contentOrUrl: any): Promise<Config> {
    let base64 = contentOrUrl
    // 如果传入的是 Vite 的资源模块对象 (src/assets)
    if (typeof contentOrUrl === 'object' && contentOrUrl.default) {
      contentOrUrl = contentOrUrl.default
    }
    if (typeof contentOrUrl === 'string' && (contentOrUrl.startsWith('/') || contentOrUrl.startsWith('http') || contentOrUrl.includes('blob:'))) {
      try {
        const response = await fetch(contentOrUrl)
        base64 = await response.text()
      } catch (e) {
        console.error('Failed to fetch preset:', e)
      }
    }
    const data = {
      Type: DriverSocketMsgType.MTCfgImport,
      Data: {
        Name: name,
        DataBase64: base64,
      },
    } as DriverSocketMsg<any>
    return this.postData(data)
  }
}
let configSet = new ConfigSet()
export { configSet }
